import React, { useState } from 'react'
import { ToggleSettingsItem } from '../toggle-settings-item'
import { DiagnosticsOrganizationSettingsLoader } from './diagnostics-organization-settings-loader'
import { ViewerLoader } from '../viewer'
import { ConfigurationPanel } from '../../legacy-design-system/configuration-panel'
import { css } from '@emotion/css'
import { ToggleOrganizationDiagnosticsMutation } from './toggle-organization-diagnostics-mutation'
import { logError, logSuccess } from '../toaster'
import { useIntl } from 'react-intl'
import { ToggleOrganizationRestrictedEditRoleMutation } from './toggle-organization-restricted-edit-role-mutation'
import { AimlOrganizationSettings } from './ai-ml/aiml-organization-settings'

interface Props {
  readonly onToggle?: () => void
}

export function GeneralOrganizationSettings({ onToggle }: Props) {
  return (
    <ViewerLoader>
      {({ viewer, loading }) => {
        return (
          !loading &&
          !!viewer && (
            <DiagnosticsOrganizationSettingsLoader
              organizationUuid={viewer.currentOrganization.id}
            >
              {({
                loading,
                diagnosticsCollectionEnabled: diagnosticsEnabled,
                restrictedEditRoleEnabled,
                isEOP,
                aimlServiceEnabled
              }) => {
                return (
                  !loading && (
                    <>
                      <AimlOrganizationSettings
                        aimlServiceEnabled={aimlServiceEnabled}
                      />
                      <GeneralOrganizationSettingsPanel
                        onToggle={onToggle}
                        diagnosticsEnabled={diagnosticsEnabled}
                        restrictedEditRoleEnabled={restrictedEditRoleEnabled}
                        isEOP={isEOP}
                        orgID={viewer.currentOrganization.id}
                      />
                    </>
                  )
                )
              }}
            </DiagnosticsOrganizationSettingsLoader>
          )
        )
      }}
    </ViewerLoader>
  )
}

export function GeneralOrganizationSettingsPanel({
  onToggle,
  diagnosticsEnabled: initDiagnosticsEnabled,
  restrictedEditRoleEnabled: initRestrictedEditRoleEnabled,
  isEOP,
  orgID
}: {
  readonly onToggle?: () => void
  readonly diagnosticsEnabled: boolean
  readonly restrictedEditRoleEnabled: boolean
  readonly isEOP: boolean
  readonly orgID: string
}) {
  const [diagnosticsEnabled, setDiagnosticsEnabled] = useState(
    initDiagnosticsEnabled
  )
  const [restrictedEditRoleEnabled, setRestrictedEditRoleEnabled] = useState(
    initRestrictedEditRoleEnabled
  )

  const { formatMessage } = useIntl()

  const linkUrlId = 'link_organizationDiagnostics'
  const title = formatMessage({ id: 'title_contrastDiagnostics' })
  const info = formatMessage({
    id: 'detail_serversSettings_appliesToAllEnvironments'
  })
  const description = formatMessage({
    id: 'detail_contrastDiagnosticsDescription'
  })
  const descriptionLink = formatMessage({
    id: 'detail_learnMoreCapitalized'
  })
  const successMessage = formatMessage({
    id: 'detail_organizationSettings_updateDiagnosticsSuccessMsg'
  })
  const errorMessage = formatMessage({
    id: 'detail_organizationSettings_updateDiagnosticsErrorMsg'
  })
  const titleRestrictedEditRole = formatMessage({
    id: 'title_restrictedEditRole'
  })
  const infoRestrictedEditRole = formatMessage({
    id: 'detail_restrictedEditRole_appliesToAllEditUsers'
  })
  const detailRestrictedEditRole = formatMessage({
    id: 'detail_restrictedEditRole'
  })
  return (
    <ConfigurationPanel title="title_orgSettings_generalSettings">
      <span
        className={css`
          padding: 20px 35px 40px;
        `}
      >
        <ToggleOrganizationDiagnosticsMutation>
          {(toggleOrganizationDiagnostics) => (
            <ToggleSettingsItem
              title={title}
              description={description}
              descriptionLink={descriptionLink}
              info={info}
              onToggle={async (enabled) => {
                try {
                  await toggleOrganizationDiagnostics({
                    variables: {
                      organizationID: orgID,
                      enabled
                    }
                  })
                  logSuccess(successMessage)
                  setDiagnosticsEnabled(enabled)
                  if (!!onToggle) {
                    onToggle()
                  }
                } catch (_) {
                  logError(errorMessage)
                }
              }}
              enabled={diagnosticsEnabled}
            />
          )}
        </ToggleOrganizationDiagnosticsMutation>
        <ToggleOrganizationRestrictedEditRoleMutation>
          {(toggleOrganizationRestrictedEditRole) => (
            <ToggleSettingsItem
              title={titleRestrictedEditRole}
              description={detailRestrictedEditRole}
              info={infoRestrictedEditRole}
              show={isEOP}
              linkUrlId={linkUrlId}
              onToggle={async (enabled) => {
                try {
                  await toggleOrganizationRestrictedEditRole({
                    variables: {
                      organizationID: orgID,
                      enabled
                    }
                  })
                  logSuccess(successMessage)
                  setRestrictedEditRoleEnabled(enabled)
                  if (!!onToggle) {
                    onToggle()
                  }
                } catch (_) {
                  logError(errorMessage)
                }
              }}
              enabled={restrictedEditRoleEnabled}
            />
          )}
        </ToggleOrganizationRestrictedEditRoleMutation>
      </span>
    </ConfigurationPanel>
  )
}
